export const SERVER_NODE_ROUTE_RETRY_AFTER_SEC = 5;

export type ServerPlatformNodeRouteRecord = {
  id: string;
  status: string;
  internalOrigin: string;
  lastHeartbeatAt?: Date | string | null;
};

export type ServerAppNodeRouteRecord = {
  name: string;
  nodeId?: string | null;
  port: number;
};

export type ServerAppNodeRouteTarget =
  | { kind: "local"; appName: string; port: number }
  | { kind: "remote_node"; appName: string; nodeId: string; origin: string }
  | { kind: "unavailable"; reason: "not_found" | "node_offline" };

const PLATFORM_CONTROL_PLANE_APP_NAME_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
const NODE_PROXY_CONTROL_PLANE_APP_HEADER = "X-Gencow-Control-Plane-App";

function normalizeControlPlaneAppName(value: string | null | undefined): string | null {
  const appName = value?.trim() ?? "";
  return PLATFORM_CONTROL_PLANE_APP_NAME_REGEX.test(appName) ? appName : null;
}

export function resolvePlatformControlPlaneAppNameFromPath(originalPath: string): string | null {
  let url: URL;
  try {
    url = new URL(`http://gencow.internal${originalPath}`);
  } catch {
    return null;
  }

  const match = url.pathname.match(/^\/platform\/apps\/([^/]+)\/(?:deploy|deploy-static)$/);
  if (!match?.[1]) return null;

  let appName: string;
  try {
    appName = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  return normalizeControlPlaneAppName(appName);
}

export function resolvePlatformControlPlaneAppNameFromProxyHeaders(
  originalPath: string,
  headers: Headers,
): string | null {
  let url: URL;
  try {
    url = new URL(`http://gencow.internal${originalPath}`);
  } catch {
    return null;
  }
  if (url.pathname !== "/api/mutation" || url.search) return null;
  return normalizeControlPlaneAppName(headers.get(NODE_PROXY_CONTROL_PLANE_APP_HEADER));
}

function parseIpv4(host: string): number[] | null {
  const parts = host.split(".");
  if (parts.length !== 4) return null;
  const octets = parts.map((part) => {
    if (!/^\d+$/.test(part)) return NaN;
    const value = Number(part);
    return Number.isInteger(value) && value >= 0 && value <= 255 ? value : NaN;
  });
  return octets.every((value) => Number.isInteger(value)) ? octets : null;
}

function isAllowedPrivateIpv4(octets: number[]): boolean {
  const [a, b] = octets;
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  return false;
}

export function normalizeServerPlatformNodeInternalOrigin(rawOrigin: string): string | null {
  let url: URL;
  try {
    url = new URL(rawOrigin);
  } catch {
    return null;
  }

  if (url.protocol !== "http:" || url.username || url.password) return null;
  if (url.pathname !== "/" || url.search || url.hash || !url.port) return null;

  const octets = parseIpv4(url.hostname);
  if (!octets || !isAllowedPrivateIpv4(octets)) return null;

  return `http://${url.hostname}:${url.port}`;
}

export function buildServerPlatformNodeRouteRecord(row: {
  id: string;
  status: string;
  internalOrigin: string;
  lastHeartbeatAt?: Date | string | null;
}): ServerPlatformNodeRouteRecord | null {
  const internalOrigin = normalizeServerPlatformNodeInternalOrigin(row.internalOrigin);
  if (!internalOrigin) return null;
  return {
    id: row.id,
    status: row.status,
    internalOrigin,
    lastHeartbeatAt: row.lastHeartbeatAt,
  };
}

export function resolveServerAppNodeRouteTarget(params: {
  appName: string;
  currentNodeId: string | null | undefined;
  app: ServerAppNodeRouteRecord | null | undefined;
  homeNode?: ServerPlatformNodeRouteRecord | null;
  localPort?: number | null;
  nowMs?: number;
  staleAfterMs?: number;
}): ServerAppNodeRouteTarget {
  if (!params.app) {
    return { kind: "unavailable", reason: "not_found" };
  }

  const currentNodeId = params.currentNodeId?.trim() || null;
  const appName = params.app.name || params.appName;
  const appNodeId = params.app.nodeId?.trim() || currentNodeId;
  if (!currentNodeId || !appNodeId || appNodeId === currentNodeId) {
    return { kind: "local", appName, port: params.localPort ?? params.app.port };
  }

  const homeNode = params.homeNode;
  if (
    !homeNode ||
    (homeNode.status !== "active" && homeNode.status !== "draining") ||
    isServerPlatformNodeHeartbeatStale(homeNode.lastHeartbeatAt, params.nowMs, params.staleAfterMs)
  ) {
    return { kind: "unavailable", reason: "node_offline" };
  }

  return {
    kind: "remote_node",
    appName,
    nodeId: homeNode.id,
    origin: homeNode.internalOrigin,
  };
}

export function isServerPlatformNodeHeartbeatStale(
  lastHeartbeatAt: Date | string | null | undefined,
  nowMs = Date.now(),
  staleAfterMs = 60_000,
): boolean {
  if (!lastHeartbeatAt || staleAfterMs <= 0) return false;
  const heartbeatMs =
    lastHeartbeatAt instanceof Date ? lastHeartbeatAt.getTime() : Date.parse(lastHeartbeatAt);
  if (!Number.isFinite(heartbeatMs)) return true;
  return nowMs - heartbeatMs > staleAfterMs;
}

export function buildHomeNodeUnavailableBody(reason: string) {
  return {
    error: "앱이 배정된 서버가 현재 요청을 처리할 수 없습니다.",
    code: "APP_HOME_NODE_UNAVAILABLE",
    status: reason,
  };
}

export async function maybeProxyRemoteServerAppRequest(params: {
  c: any;
  host: string;
  appName: string;
  appRow: ServerAppNodeRouteRecord;
  currentNodeId?: string | null;
  upgradeHeader: string;
  getLivePort(appName: string): number | undefined;
  loadAppRouteNode?(nodeId: string): Promise<ServerPlatformNodeRouteRecord | null>;
  createProxyRequestReplayPlan(request: Request): Promise<{ initialBody: BodyInit | undefined }>;
  markLastAccess(appName: string): void;
  proxyRemoteNodeRequest?(request: {
    c: any;
    host: string;
    appName: string;
    nodeId: string;
    origin: string;
    body?: BodyInit;
  }): Promise<Response>;
  buildTransientProxyFailureBody(): unknown;
  logger: Pick<Console, "warn" | "error">;
}): Promise<Response | null> {
  const currentNodeId = params.currentNodeId?.trim() || null;
  if (
    params.upgradeHeader === "websocket" ||
    !currentNodeId ||
    !params.loadAppRouteNode ||
    !params.proxyRemoteNodeRequest
  ) {
    return null;
  }

  const appNodeId = params.appRow.nodeId?.trim() || currentNodeId;
  let homeNode: ServerPlatformNodeRouteRecord | null = null;
  if (appNodeId !== currentNodeId) {
    try {
      homeNode = await params.loadAppRouteNode(appNodeId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger.warn(`[platform] ${params.appName}: failed to load home node ${appNodeId}: ${message}`);
    }
  }

  const routeTarget = resolveServerAppNodeRouteTarget({
    appName: params.appName,
    currentNodeId,
    app: params.appRow,
    homeNode,
    localPort: params.getLivePort(params.appName) ?? null,
  });

  if (routeTarget.kind === "remote_node") {
    const replayPlan = await params.createProxyRequestReplayPlan(params.c.req.raw);
    try {
      params.markLastAccess(params.appName);
    } catch {
      /* ignore */
    }
    try {
      return await params.proxyRemoteNodeRequest({
        c: params.c,
        host: params.host,
        appName: params.appName,
        nodeId: routeTarget.nodeId,
        origin: routeTarget.origin,
        body: replayPlan.initialBody,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger.error(
        `[platform] ${params.appName}: remote node proxy failed to ${routeTarget.nodeId}: ${message}`,
      );
      return params.c.json(params.buildTransientProxyFailureBody(), 502);
    }
  }

  if (routeTarget.kind === "unavailable") {
    if (routeTarget.reason === "not_found") {
      return params.c.json({ error: `App "${params.appName}" not found` }, 404);
    }
    params.c.header("Retry-After", String(SERVER_NODE_ROUTE_RETRY_AFTER_SEC));
    return params.c.json(buildHomeNodeUnavailableBody(routeTarget.reason), 503);
  }

  return null;
}

export async function maybeUpgradeRemoteServerAppWebSocket(params: {
  c: any;
  host: string;
  appName: string;
  appRow: ServerAppNodeRouteRecord;
  currentNodeId?: string | null;
  upgradeHeader: string;
  getLivePort(appName: string): number | undefined;
  loadAppRouteNode?(nodeId: string): Promise<ServerPlatformNodeRouteRecord | null>;
  proxyRemoteNodeWebSocket?(request: {
    c: any;
    host: string;
    appName: string;
    nodeId: string;
    origin: string;
  }): Promise<Response>;
  logger: Pick<Console, "warn">;
}): Promise<Response | null> {
  const currentNodeId = params.currentNodeId?.trim() || null;
  if (
    params.upgradeHeader !== "websocket" ||
    !currentNodeId ||
    !params.loadAppRouteNode ||
    !params.proxyRemoteNodeWebSocket
  ) {
    return null;
  }

  const appNodeId = params.appRow.nodeId?.trim() || currentNodeId;
  let homeNode: ServerPlatformNodeRouteRecord | null = null;
  if (appNodeId !== currentNodeId) {
    try {
      homeNode = await params.loadAppRouteNode(appNodeId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger.warn(`[platform] ${params.appName}: failed to load home node ${appNodeId}: ${message}`);
    }
  }

  const routeTarget = resolveServerAppNodeRouteTarget({
    appName: params.appName,
    currentNodeId,
    app: params.appRow,
    homeNode,
    localPort: params.getLivePort(params.appName) ?? null,
  });

  if (routeTarget.kind === "remote_node") {
    return params.proxyRemoteNodeWebSocket({
      c: params.c,
      host: params.host,
      appName: params.appName,
      nodeId: routeTarget.nodeId,
      origin: routeTarget.origin,
    });
  }

  if (routeTarget.kind === "unavailable") {
    if (routeTarget.reason === "not_found") {
      return params.c.json({ error: `App "${params.appName}" not found` }, 404);
    }
    params.c.header("Retry-After", String(SERVER_NODE_ROUTE_RETRY_AFTER_SEC));
    return params.c.json(buildHomeNodeUnavailableBody(routeTarget.reason), 503);
  }

  return null;
}

export function isSignedNodeProxyRequestPath(pathname: string, headers: Headers): boolean {
  return pathname.startsWith("/internal/node-proxy/") && headers.get("X-Gencow-Node-Proxy") === "v1";
}

function sanitizeNodeProxyLoopbackHeaders(headers: Headers): Headers {
  const sanitized = new Headers();
  for (const [key, value] of headers.entries()) {
    const normalized = key.toLowerCase();
    if (
      normalized.startsWith("x-gencow-node-") ||
      normalized === "x-internal-token" ||
      normalized === "x-cron-token"
    ) {
      continue;
    }
    sanitized.append(key, value);
  }
  return sanitized;
}

function shouldPreserveControlPlaneNodeProxyHeaders(originalPath: string, headers: Headers): boolean {
  return (
    resolvePlatformControlPlaneAppNameFromProxyHeaders(originalPath, headers) !== null &&
    headers.get("X-Gencow-Node-Proxy") === "v1"
  );
}

export function createServerNodeProxyLoopbackRequest(params: {
  c: any;
  originalHost: string;
  originalPath: string;
  body?: BodyInit;
}): Request {
  const headers = shouldPreserveControlPlaneNodeProxyHeaders(params.originalPath, params.c.req.raw.headers)
    ? new Headers(params.c.req.raw.headers)
    : sanitizeNodeProxyLoopbackHeaders(params.c.req.raw.headers);
  headers.set("Host", params.originalHost);

  const init: RequestInit & { duplex?: "half" } = {
    method: params.c.req.method,
    headers,
    body: params.body,
    redirect: "manual",
  };
  if (params.body && params.c.req.method !== "GET" && params.c.req.method !== "HEAD") {
    init.duplex = "half";
  }

  return new Request(`http://${params.originalHost}${params.originalPath}`, init);
}
